import { useMemo } from 'react';
import { toastError, toastSuccess } from 'partial/components/react-geek-toast';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import {
  getDashboardGraph,
  getOrgWallet,
  getSubscriberIntegrations,
  getSubscriberMaskNames,
  getSubscriberMessages,
  getSubscriberTxnHistory,
  getSubscriberRateHistory,
  getSubscriberUpgradeHistory,
  getTotalInteg,
  getTotalMasks,
  getTotalMsgs,
  showSubscriberSubscription,
  banAccount,
  banExempt,
  getOrganizations,
  editRate,
  getDomainList,
  editDomain,
  addDomain,
  deleteDomain,
  getOrgBanLogs,
} from './actions';

const type = {
  hour: 'DAILY',
  day: 'MONTHLY',
  month: 'YEARLY',
};

export const useEditRate = (cb) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(editRate, {
    onSuccess: () => {
      queryClient.invalidateQueries('show-subscriber-info');
      queryClient.invalidateQueries('get-org-rate-history');
      toastSuccess('Successfully updated rate ratio!');
      if (typeof cb === 'function') cb();
    },
    onError: (err) => {
      toastError(err?.response?.data?.message);
      if (typeof cb === 'function') cb();
    },
  });
  return [isLoading, mutate];
};

export const useOrganizationList = (filter) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-organization-list', filter],
    queryFn: getOrganizations,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};

export const useBanAccount = (cb) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(banAccount, {
    onSuccess: (res) => {
      const response = res?.data?.data;
      const isBanned = response?.is_banned;
      const label = isBanned ? 'banned' : 'unbanned';
      queryClient.invalidateQueries('show-subscriber-info');
      queryClient.invalidateQueries('get-organization-list');
      setTimeout(() => {
        queryClient.invalidateQueries('get-org-ban-logs');
      }, 5000);
      toastSuccess(`Successfully ${label} account!`);
      if (typeof cb === 'function') cb();
    },
    onError: (err) => {
      toastError(err?.response?.data?.message);
      if (typeof cb === 'function') cb();
    },
  });
  return [isLoading, mutate];
};

export const useBanExempt = (cb) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(banExempt, {
    onSuccess: (res) => {
      const response = res?.data?.data;
      const isBanExempted = response?.is_ban_exempted;
      const label = isBanExempted ? 'exempted' : 'unexempted';
      queryClient.invalidateQueries('show-subscriber-info');
      toastSuccess(`Successfully ban ${label}`);
      if (typeof cb === 'function') cb();
    },
    onError: (err) => {
      toastError(err?.response?.data?.message);
      if (typeof cb === 'function') cb();
    },
  });
  return [isLoading, mutate];
};

export const useShowSubscriberSubscription = (id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['show-subscriber-subscription', id],
    queryFn: showSubscriberSubscription,
    retry: false,
    keepPreviousData: false,
    enabled: !!id,
  });
  const data = raw?.data?.data;
  return [isLoading, data];
};

export const useShowOrgWallet = (id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['show-organization-wallet', id],
    queryFn: getOrgWallet,
    retry: false,
    keepPreviousData: false,
    enabled: !!id,
  });
  const data = raw?.data?.data;
  return [isLoading, data];
};

export const useMessagesHistogram = (filter, id) => {
  const params = useMemo(
    () => ({
      date_from: filter?.from,
      date_to: filter?.to,
      type: type[filter?.type],
    }),
    [filter],
  );
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-subscriber-dashboard-graph', id, params],
    queryFn: getDashboardGraph,
    retry: false,
    keepPreviousData: false,
  });
  const data = raw?.data?.data;
  const { table, chart } = useMemo(() => {
    const tableData = (data || []).map((x) => ({
      id: x.label,
      name: x.label,
      messages: x.count,
    }));
    const chartData = (data || []).map((x) => ({
      id: x.label,
      title: x.label,
      count: x.count,
    }));
    return {
      table: tableData,
      chart: chartData,
    };
  }, [data]);
  return [isLoading, table, chart];
};

export const useTotalMsgs = (id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-total-subscriber-msgs', id],
    queryFn: getTotalMsgs,
    retry: false,
    keepPreviousData: false,
  });
  const data = raw?.data?.data;
  return [isLoading, data];
};

export const useTotalInteg = (id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-total-subscriber-integrations', id],
    queryFn: getTotalInteg,
    retry: false,
    keepPreviousData: false,
  });
  const data = raw?.data?.data;
  return [isLoading, data];
};

export const useTotalMasks = (id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-total-subscriber-masks', id],
    queryFn: getTotalMasks,
    retry: false,
    keepPreviousData: false,
  });
  const data = raw?.data?.data;
  return [isLoading, data];
};

export const useOrgMaskNameList = (filter, id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-org-mask-name-list', id, filter],
    queryFn: getSubscriberMaskNames,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};

export const useOrgIntegrationList = (filter, id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-org-integration-list', id, filter],
    queryFn: getSubscriberIntegrations,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};

export const useOrgUpgradeHistory = (filter, id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-org-upgrade-history', id, filter],
    queryFn: getSubscriberUpgradeHistory,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};

export const useOrgTxnHistory = (filter, id) => {
  const {
    data: raw,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['get-org-transaction-history', id, filter],
    queryFn: getSubscriberTxnHistory,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta, refetch];
};

export const useRateHistory = (filter, id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-org-rate-history', id, filter],
    queryFn: getSubscriberRateHistory,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};

export const useOrgMessageList = (filter, id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-org-message-list', id, filter],
    queryFn: getSubscriberMessages,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};

export const useDomainList = (uuid) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-org-domain-list', uuid],
    queryFn: getDomainList,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};

export const useAddDomain = (cb) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(addDomain, {
    onSuccess: () => {
      queryClient.invalidateQueries('get-org-domain-list');
      toastSuccess('Successfully added domain');
      if (typeof cb === 'function') cb();
    },
    onError: (err) => {
      toastError(err?.response?.data?.message);
      if (typeof cb === 'function') cb();
    },
  });
  return [isLoading, mutate];
};

export const useEditDomain = (cb) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(editDomain, {
    onSuccess: () => {
      queryClient.invalidateQueries('get-org-domain-list');
      toastSuccess('Successfully edited domain');
      if (typeof cb === 'function') cb();
    },
    onError: (err) => {
      toastError(err?.response?.data?.message);
      if (typeof cb === 'function') cb();
    },
  });
  return [isLoading, mutate];
};

export const useDeleteDomain = (cb) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(deleteDomain, {
    onSuccess: () => {
      queryClient.invalidateQueries('get-org-domain-list');
      toastSuccess('Successfully removed domain');
      if (typeof cb === 'function') cb();
    },
    onError: (err) => {
      toastError(err?.response?.data?.message);
      if (typeof cb === 'function') cb();
    },
  });
  return [isLoading, mutate];
};

export const useOrgBanLogs = (filter, id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-org-ban-logs', id, filter],
    queryFn: getOrgBanLogs,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};
